// Tours.jsx (or SriLankaTours.jsx - renamed for clarity)
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// === IMAGES ===
// Replace these with your actual assets (hero + temple-specific)
import toursHeroBg from '../assets/Tours/1.jpg';
import srilanka from '../assets/Ayurveda/tourism.png'          // Hero background (keep or update)
import kandeViharaImg from '../assets/Tours/585050.jpg'; // Add real temple image here
import bentotaRiverImg from '../assets/Tours/bentota-river-safari.jpg';
import galleOldCityImg from '../assets/Tours/galle-dutch-fort-sri-lanka-header-1440x1440.jpg';
import sigiriyaImg from '../assets/Tours/Sigiriya-Rock-Sri-Lanka.jpg';
import kandyCulturalImg from '../assets/Tours/chathura-anuradha-subasinghe-40uQmE9Zq8g-unsplash-1024x683.jpg';
// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const Tours = () => {
    const { t } = useTranslation('tours');

    // Translation keys for intro paragraphs
    const introParagraphs = [
        t('introPara1'),
        t('introPara2'),
        t('introPara3')
    ];

    const kandeViharaDetails = [
        t('kandeDetail1'),
        t('kandeDetail2'),
        t('kandeDetail3'),
        t('kandeDetail4'),
    ];

    const bentotaRiverDetails = [
        t('bentotaDetail1'),
        t('bentotaDetail2'),
        t('bentotaDetail3'),
        t('bentotaDetail4'),
        t('bentotaDetail5'),
    ];
    const galleOldCityDetails = [
        t('galleDetail1'),
        t('galleDetail2'),
        t('galleDetail3'),
        t('galleDetail4'),
        t('galleDetail5'),
    ];
    const sigiriyaDetails = [
        t('sigiriyaDetail1'),
        t('sigiriyaDetail2'),
        t('sigiriyaDetail3'),
        t('sigiriyaDetail4'),
        t('sigiriyaDetail5'),
    ];
    const kandyCulturalDetails = [
        t('kandyDetail1'),
        t('kandyDetail2'),
        t('kandyDetail3'),
        t('kandyDetail4'),
        t('kandyDetail5'),
    ];

    return (
        <div className="bg-[#1F1F1F] min-h-screen">
            {/* ===== HERO SECTION ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[70vh] lg:h-[85vh]"
            >
                <img
                    src={toursHeroBg}
                    alt="Sri Lanka Tours & Excursions Hero"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] via-[#00000080] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-16 md:pt-20">
                    <div className="w-full max-w-4xl text-center">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-tight text-white font-copperplate">
                            {t('heroTitle')}
                        </h1>
                        <p className="mt-6 text-lg md:text-2xl lg:text-3xl font-semibold tracking-wide text-[#96BA33] font-lato">
                            {t('heroSubtitle')}
                        </p>

                        {/* Decorative SVG lines - kept from original */}
                        <div className="absolute left-8 top-10 hidden lg:block">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-12 right-8 hidden lg:block">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ===== INTRO SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-16 md:py-20"
            >
                <div className="container mx-auto px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                        <div className="w-full lg:w-1/2">
                            <img
                                src={srilanka} // or fallback to toursHeroBg if no specific image yet
                                alt={t('kandeAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl transform hover:scale-105 transition duration-500"
                                loading="lazy"
                            />
                        </div>

                        <div className="w-full lg:w-1/2 space-y-6 text-white">
                            {introParagraphs.map((paragraph, i) => (
                                <p key={i} className="text-base md:text-lg lg:text-xl leading-relaxed font-inknut">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== KANDE VIHARA TEMPLE SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24"
            >
                <div className="container mx-auto px-5 sm:px-6 lg:px-8">

                    {/* === MOBILE / SM layout: Title → Image → Text + Button === */}
                    <div className="block md:hidden space-y-8">
                        {/* Title */}
                        <h2 className="text-3xl sm:text-4xl font-semibold text-center text-white font-copperplate">
                            {t('kandeTitle')}
                        </h2>

                        {/* Image */}
                        <div className="mt-6">
                            <img
                                src={kandeViharaImg}
                                alt={t('kandeBuddhaAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>

                        {/* Text + Button */}
                        <div className="space-y-6 text-white text-base sm:text-lg leading-relaxed">
                            {kandeViharaDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                            <div className="pt-6">
                                <a
                                    href="https://wa.me/+94777382466"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition text-center w-full sm:w-auto"
                                >
                                    {t('bookTour')}
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* === DESKTOP / MD+ layout: Text left | Image right === */}
                    <div className="hidden md:block">
                        <h2 className="text-4xl lg:text-5xl font-semibold text-center text-white mb-12 md:mb-16 font-copperplate">
                            {t('kandeTitle')}
                        </h2>

                        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Text Content */}
                            <div className="space-y-6 text-white text-lg lg:text-xl leading-relaxed order-2 md:order-1">
                                {kandeViharaDetails.map((point, i) => (
                                    <p key={i} className="font-inknut">
                                        {point}
                                    </p>
                                ))}

                                <div className="pt-6">
                                    <a
                                        href="https://wa.me/+94777382466"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition"
                                    >
                                        {t('bookTour')}
                                    </a>
                                </div>
                            </div>

                            {/* Image */}
                            <div className="order-1 md:order-2">
                                <img
                                    src={kandeViharaImg}
                                    alt={t('kandeBuddhaAlt')}
                                    className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== BENTOTA RIVER BOAT SAFARI SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24"
            >
                <div className="container mx-auto px-5 sm:px-6 lg:px-8">

                    {/* === MOBILE / SM layout: Title → Image → Text + Button === */}
                    <div className="block md:hidden space-y-8">
                        {/* Title */}
                        <h2 className="text-3xl sm:text-4xl font-semibold text-center text-white font-copperplate">
                            {t('bentotaTitle')}
                        </h2>

                        {/* Image */}
                        <div className="mt-6">
                            <img
                                src={bentotaRiverImg}
                                alt={t('bentotaAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>

                        {/* Text + Button */}
                        <div className="space-y-6 text-white text-base sm:text-lg leading-relaxed">
                            {bentotaRiverDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                            <div className="pt-6">
                                <a
                                    href="https://wa.me/+94777382466"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition text-center w-full sm:w-auto"
                                >
                                    {t('bookTour')}
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* === DESKTOP / MD+ layout: Image left | Text right === */}
                    <div className="hidden md:block">
                        <h2 className="text-4xl lg:text-5xl font-semibold text-center text-white mb-12 md:mb-16 font-copperplate">
                            {t('bentotaTitle')}
                        </h2>

                        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Image - LEFT */}
                            <div className="order-1">
                                <img
                                    src={bentotaRiverImg}
                                    alt={t('bentotaAlt')}
                                    className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                    loading="lazy"
                                />
                            </div>

                            {/* Details - RIGHT */}
                            <div className="space-y-6 text-white text-lg lg:text-xl leading-relaxed order-2">
                                {bentotaRiverDetails.map((point, i) => (
                                    <p key={i} className="font-inknut">
                                        {point}
                                    </p>
                                ))}

                                <div className="pt-6">
                                    <a
                                        href="https://wa.me/+94777382466"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition"
                                    >
                                        {t('bookTour')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24"
            >
                <div className="container mx-auto px-5 sm:px-6 lg:px-8">

                    {/* === MOBILE / SM layout: Title → Image → Text + Button === */}
                    <div className="block md:hidden space-y-8">
                        {/* Title */}
                        <h2 className="text-3xl sm:text-4xl font-semibold text-center text-white font-copperplate">
                            {t('galleTitle')}
                        </h2>

                        {/* Image */}
                        <div className="mt-6">
                            <img
                                src={galleOldCityImg}
                                alt={t('galleAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>

                        {/* Text + Button */}
                        <div className="space-y-6 text-white text-base sm:text-lg leading-relaxed">
                            {galleOldCityDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                            <div className="pt-6">
                                <a
                                    href="https://wa.me/+94777382466"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition text-center w-full sm:w-auto"
                                >
                                    {t('bookTour')}
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* === DESKTOP / MD+ layout: Text left | Image right === */}
                    <div className="hidden md:block">
                        <h2 className="text-4xl lg:text-5xl font-semibold text-center text-white mb-12 md:mb-16 font-copperplate">
                            {t('galleTitle')}
                        </h2>

                        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Text Content - LEFT */}
                            <div className="space-y-6 text-white text-lg lg:text-xl leading-relaxed order-2 md:order-1">
                                {galleOldCityDetails.map((point, i) => (
                                    <p key={i} className="font-inknut">
                                        {point}
                                    </p>
                                ))}

                                <div className="pt-6">
                                    <a
                                        href="https://wa.me/+94777382466"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition"
                                    >
                                        {t('bookTour')}
                                    </a>
                                </div>
                            </div>

                            {/* Image - RIGHT */}
                            <div className="order-1 md:order-2">
                                <img
                                    src={galleOldCityImg}
                                    alt={t('galleAlt')}
                                    className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24"
            >
                <div className="container mx-auto px-5 sm:px-6 lg:px-8">

                    {/* === MOBILE / SM layout: Title → Image → Text + Button === */}
                    <div className="block md:hidden space-y-8">
                        {/* Title */}
                        <h2 className="text-3xl sm:text-4xl font-semibold text-center text-white font-copperplate">
                            {t('sigiriyaTitle')}
                        </h2>

                        {/* Image */}
                        <div className="mt-6">
                            <img
                                src={sigiriyaImg}
                                alt={t('sigiriyaAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>

                        {/* Text + Button */}
                        <div className="space-y-6 text-white text-base sm:text-lg leading-relaxed">
                            {sigiriyaDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                            <div className="pt-6">
                                <a
                                    href="https://wa.me/+94777382466"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition text-center w-full sm:w-auto"
                                >
                                    {t('bookTour')}
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* === DESKTOP / MD+ layout: Image left | Text right === */}
                    <div className="hidden md:block">
                        <h2 className="text-4xl lg:text-5xl font-semibold text-center text-white mb-12 md:mb-16 font-copperplate">
                            {t('sigiriyaTitle')}
                        </h2>

                        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Image - LEFT */}
                            <div className="order-1">
                                <img
                                    src={sigiriyaImg}
                                    alt={t('sigiriyaAlt')}
                                    className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                    loading="lazy"
                                />
                            </div>

                            {/* Details - RIGHT */}
                            <div className="space-y-6 text-white text-lg lg:text-xl leading-relaxed order-2">
                                {sigiriyaDetails.map((point, i) => (
                                    <p key={i} className="font-inknut">
                                        {point}
                                    </p>
                                ))}

                                <div className="pt-6">
                                    <a
                                        href="https://wa.me/+94777382466"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition"
                                    >
                                        {t('bookTour')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24"
            >
                <div className="container mx-auto px-5 sm:px-6 lg:px-8">

                    {/* === MOBILE / SM layout: Title → Image → Text + Button === */}
                    <div className="block md:hidden space-y-8">
                        {/* Title */}
                        <h2 className="text-3xl sm:text-4xl font-semibold text-center text-white font-copperplate">
                            {t('kandyTitle')}
                        </h2>

                        {/* Image */}
                        <div className="mt-6">
                            <img
                                src={kandyCulturalImg}
                                alt={t('kandyAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>

                        {/* Text + Button */}
                        <div className="space-y-6 text-white text-base sm:text-lg leading-relaxed">
                            {kandyCulturalDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                            <div className="pt-6">
                                <a
                                    href="https://wa.me/+94777382466"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition text-center w-full sm:w-auto"
                                >
                                    {t('bookTour')}
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* === DESKTOP / MD+ layout: Text left | Image right === */}
                    <div className="hidden md:block">
                        <h2 className="text-4xl lg:text-5xl font-semibold text-center text-white mb-12 md:mb-16 font-copperplate">
                            {t('kandyTitle')}
                        </h2>

                        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                            {/* Text Content - LEFT */}
                            <div className="space-y-6 text-white text-lg lg:text-xl leading-relaxed order-2 md:order-1">
                                {kandyCulturalDetails.map((point, i) => (
                                    <p key={i} className="font-inknut">
                                        {point}
                                    </p>
                                ))}

                                <div className="pt-6">
                                    <a
                                        href="https://wa.me/+94777382466"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-[#96BA33] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#aacc44] transition"
                                    >
                                        {t('bookTour')}
                                    </a>
                                </div>
                            </div>

                            {/* Image - RIGHT */}
                            <div className="order-1 md:order-2">
                                <img
                                    src={kandyCulturalImg}
                                    alt={t('kandyAlt')}
                                    className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default Tours;